import moment from 'moment-timezone';

import { postEvent, getVenue } from '../../api';
import eventTypes from '../../constants/eventTypes';
import { transformResponsiveImages } from '../../transformers';

const { GENERAL_ADMISSION, SEATED } = eventTypes;
const eventTypeMap = {
  1: GENERAL_ADMISSION,
  2: SEATED,
};

const getEventMinAndMaxPartySizes = (
  { size } = {},
  { maxPartySize, minPartySize } = {},
  { eventMaxPartySize, eventMinPartySize } = {}
) => ({
  maxPartySize: eventMaxPartySize || size?.max || maxPartySize,
  minPartySize: eventMinPartySize || size?.min || minPartySize,
});

const normalizeVenueForEventDetails = (venue = {}) => ({
  ...venue,
  id: typeof venue.id === 'number' ? { resy: venue.id } : venue.id,
  contact: {
    ...(venue.contact || {}),
    phone_number: venue?.contact?.phone_number || venue.phone_number,
    url: venue?.contact?.url || venue.url,
  },
  location: {
    ...(venue.location || {}),
    address_1: venue?.location?.address_1 || venue.address_1,
    address_2: venue?.location?.address_2 || venue.address_2,
    locality: venue?.location?.locality || venue.locality,
    postal_code: venue?.location?.postal_code || venue.postal_code,
    region: venue?.location?.region || venue.region,
  },
});

const getEventDetails = (args) => {
  const params = {
    location: args.location,
    venue_url_slug: args.venue_url_slug,
    event_url_slug: args.event_url_slug,
    num_seats: args.num_seats || 2,
    preview_token: args.previewToken,
  };

  if (args.resyToken) params.resy_token = args.resyToken;

  return postEvent(params).then((result) => {
    const now = moment();
    const { event, status } = result;
    let noIndex = false;

    if (status === 404 || now.diff(event?.date, 'days') >= 3) {
      noIndex = true;
    }
    const {
      date,
      enhanced_description: descriptionMd,
      description,
      end_time: endTime,
      event_type: eventType,
      enhanced_need_to_know: needToKnowMd,
      general_sale_date: generalSaleDate,
      pre_sale_dates: presaleDates,
      packages: ticketPackages,
      is_presale: isPresale,
      is_online: isOnline,
      max_party_size: maxPartySize,
      min_party_size: minPartySize,
      name,
      need_to_know: needToKnow,
      notify_active: notifyActive,
      notify_options: notifyOptions,
      preview,
      price_summary: priceSummary,
      responsive_images: responseImages,
      service_type_id: serviceTypeId,
      shift_id: eventId,
      venue,
      why_we_like_it: whyWeLikeIt,
      time,
    } = event;

    moment.tz.setDefault(args.timezone);

    const options = notifyOptions && notifyOptions[0] ? notifyOptions[0] : {};

    const notifyStart = moment(options.min_request_datetime, 'YYYY-MM-DD HH:mm:ss');
    const notifyDate = notifyStart.format('YYYY-MM-DD');
    const notifyStartTime = notifyStart.format('HH:mm:ss');
    const notifyEnd = moment(options.max_request_datetime, 'YYYY-MM-DD HH:mm:ss');
    const notifyEndTime = notifyEnd.format('HH:mm:ss');

    const normalizedVenue = normalizeVenueForEventDetails(venue);

    return {
      aboutCopy: descriptionMd || description,
      date,
      description: preview,
      endTime,
      eventId,
      hasNotify: !!notifyActive,
      images: event.images,
      isOnline,
      isGlobalDiningAccess: event.is_global_dining_access,
      isPresale,
      generalSaleDate,
      packages: ticketPackages,
      priceSummary,
      ticketPackages,
      presaleDates,
      location: {
        address1: normalizedVenue.location.address_1,
        city: normalizedVenue.location.locality,
        neighborhood: normalizedVenue.neighborhood,
        state: normalizedVenue.location.region,
        postalCode: normalizedVenue.location.postal_code,
      },
      maxPartySize,
      name: venue.name,
      needToKnowCopy: needToKnowMd || needToKnow,
      noIndex,
      notifyDate,
      notifyEndTime,
      notifyStartTime,
      phone: normalizedVenue.contact.phone_number,
      saleHasEnded: now.diff(notifyEnd) > 0,
      serviceTypeId,
      slides: transformResponsiveImages(responseImages, '16:9', 800),
      time,
      timezone: normalizedVenue?.locale?.time_zone,
      title: name,
      type: eventTypeMap[eventType],
      url: normalizedVenue.contact.url,
      venue: normalizedVenue.name,
      venueData: normalizedVenue,
      venueId: typeof normalizedVenue.id === 'object' ? normalizedVenue.id?.resy : normalizedVenue.id,
      whyWeLikeIt,
      ...getEventMinAndMaxPartySizes(event.packages?.[0]?.party, args.venueMaxMinSizeDetails, {
        eventMaxPartySize: maxPartySize,
        eventMinPartySize: minPartySize,
      }),
    };
  })
    .catch((error) => {
      throw error;
    });
};

const getVenueDetails = (params) => {
  const {
    location,
    venue_url_slug: venueSlug,
  } = params;

  return getVenue({
    location,
    url_slug: venueSlug,
  }).then(
    (result) => {
      const data = result;
      return data;
    }
  );
};

export {
  getEventDetails,
  getVenueDetails,
};
